import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Workflow, ApprovalLevel, Role, User, Status, EventType, Group, CreateWorkflowRequest, UpdateWorkflowRequest, CreateApprovalLevelRequest, UpdateApprovalLevelRequest } from '../../services/workflowApi';

// State interfaces
export interface WorkflowState {
  // Data
  workflows: Workflow[];
  selectedWorkflow: Workflow | null;
  roles: Role[];
  users: User[];
  statuses: Status[];
  eventTypes: EventType[];
  groups: Group[];
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  isEditing: boolean;
  isDialogOpen: boolean;
  validationErrors: string[];
  
  // Approval Level Management
  isApprovalLevelDialogOpen: boolean;
  selectedApprovalLevel: ApprovalLevel | null;
  isEditingApprovalLevel: boolean;
  approvalLevelValidationErrors: string[];
  newApprovalLevels: ApprovalLevel[]; // For newly added levels before save
  
  // List State
  searchTerm: string;
  filters: {
    active: string; // 'all', 'true', 'false'
    roleId: number | null;
  };
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedWorkflowIds: number[];
  bulkLoading: boolean;
}

const initialState: WorkflowState = {
  workflows: [],
  selectedWorkflow: null,
  roles: [],
  users: [],
  statuses: [],
  eventTypes: [],
  groups: [],
  loading: false,
  error: null,
  isEditing: false,
  isDialogOpen: false,
  validationErrors: [],
  isApprovalLevelDialogOpen: false,
  selectedApprovalLevel: null,
  isEditingApprovalLevel: false,
  approvalLevelValidationErrors: [],
  newApprovalLevels: [],
  searchTerm: '',
  filters: {
    active: 'all',
    roleId: null,
  },
  pagination: {
    page: 1,
    pageSize: 25,
    total: 0,
  },
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  selectedWorkflowIds: [],
  bulkLoading: false,
};

const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setBulkLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data actions
    setWorkflows: (state, action: PayloadAction<Workflow[]>) => {
      state.workflows = action.payload;
      state.pagination.total = action.payload.length;
    },
    setSelectedWorkflow: (state, action: PayloadAction<Workflow | null>) => {
      state.selectedWorkflow = action.payload;
      // Reset new approval levels when selecting a different workflow
      state.newApprovalLevels = [];
    },
    setRoles: (state, action: PayloadAction<Role[]>) => {
      state.roles = action.payload;
    },
    setUsers: (state, action: PayloadAction<User[]>) => {
      state.users = action.payload;
    },
    setStatuses: (state, action: PayloadAction<Status[]>) => {
      state.statuses = action.payload;
    },
    setEventTypes: (state, action: PayloadAction<EventType[]>) => {
      state.eventTypes = action.payload;
    },
    setGroups: (state, action: PayloadAction<Group[]>) => {
      state.groups = action.payload;
    },
    addWorkflow: (state, action: PayloadAction<Workflow>) => {
      state.workflows.push(action.payload);
      state.pagination.total += 1;
    },
    updateWorkflow: (state, action: PayloadAction<Workflow>) => {
      const index = state.workflows.findIndex(workflow => workflow.Id === action.payload.Id);
      if (index !== -1) {
        state.workflows[index] = action.payload;
      }
    },
    removeWorkflow: (state, action: PayloadAction<number>) => {
      state.workflows = state.workflows.filter(workflow => workflow.Id !== action.payload);
      state.pagination.total -= 1;
    },
    
    // Approval Level management
    addApprovalLevel: (state, action: PayloadAction<ApprovalLevel>) => {
      // Add to selected workflow if applicable
      if (state.selectedWorkflow && state.selectedWorkflow.Id === action.payload.WorkFlowSetupId) {
        if (!state.selectedWorkflow.ApprovalLevels) {
          state.selectedWorkflow.ApprovalLevels = [];
        }
        state.selectedWorkflow.ApprovalLevels.push(action.payload);
      }
    },
    updateApprovalLevel: (state, action: PayloadAction<ApprovalLevel>) => {
      // Update in selected workflow if applicable
      if (state.selectedWorkflow?.ApprovalLevels) {
        const index = state.selectedWorkflow.ApprovalLevels.findIndex(level => level.Id === action.payload.Id);
        if (index !== -1) {
          state.selectedWorkflow.ApprovalLevels[index] = action.payload;
        }
      }
      // Update in new approval levels if applicable
      const newIndex = state.newApprovalLevels.findIndex(level => level.Id === action.payload.Id);
      if (newIndex !== -1) {
        state.newApprovalLevels[newIndex] = action.payload;
      }
    },
    removeApprovalLevel: (state, action: PayloadAction<number>) => {
      // Remove from selected workflow if applicable
      if (state.selectedWorkflow?.ApprovalLevels) {
        state.selectedWorkflow.ApprovalLevels = state.selectedWorkflow.ApprovalLevels.filter(level => level.Id !== action.payload);
      }
      // Remove from new approval levels if applicable
      state.newApprovalLevels = state.newApprovalLevels.filter(level => level.Id !== action.payload);
    },
    addNewApprovalLevel: (state, action: PayloadAction<ApprovalLevel>) => {
      state.newApprovalLevels.push(action.payload);
    },
    removeNewApprovalLevel: (state, action: PayloadAction<number>) => {
      state.newApprovalLevels = state.newApprovalLevels.filter((_, index) => index !== action.payload);
    },
    clearNewApprovalLevels: (state) => {
      state.newApprovalLevels = [];
    },
    
    // Dialog and form actions
    setDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isDialogOpen = action.payload;
      if (!action.payload) {
        state.selectedWorkflow = null;
        state.isEditing = false;
        state.validationErrors = [];
        state.newApprovalLevels = [];
      }
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    
    // Approval Level dialog actions
    setApprovalLevelDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isApprovalLevelDialogOpen = action.payload;
      if (!action.payload) {
        state.selectedApprovalLevel = null;
        state.isEditingApprovalLevel = false;
        state.approvalLevelValidationErrors = [];
      }
    },
    setSelectedApprovalLevel: (state, action: PayloadAction<ApprovalLevel | null>) => {
      state.selectedApprovalLevel = action.payload;
    },
    setEditingApprovalLevel: (state, action: PayloadAction<boolean>) => {
      state.isEditingApprovalLevel = action.payload;
    },
    setApprovalLevelValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.approvalLevelValidationErrors = action.payload;
    },
    
    // Search and filters
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.pagination.page = 1; // Reset to first page when searching
    },
    setFilters: (state, action: PayloadAction<Partial<WorkflowState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filtering
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<WorkflowState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<WorkflowState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedWorkflowIds: (state, action: PayloadAction<number[]>) => {
      state.selectedWorkflowIds = action.payload;
    },
    toggleWorkflowSelection: (state, action: PayloadAction<number>) => {
      const workflowId = action.payload;
      const isSelected = state.selectedWorkflowIds.includes(workflowId);
      if (isSelected) {
        state.selectedWorkflowIds = state.selectedWorkflowIds.filter(id => id !== workflowId);
      } else {
        state.selectedWorkflowIds.push(workflowId);
      }
    },
    selectAllWorkflows: (state) => {
      state.selectedWorkflowIds = state.workflows.map(workflow => workflow.Id);
    },
    deselectAllWorkflows: (state) => {
      state.selectedWorkflowIds = [];
    },
    
    // Reset state
    resetState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setBulkLoading,
  setError,
  setWorkflows,
  setSelectedWorkflow,
  setRoles,
  setUsers,
  setStatuses,
  setEventTypes,
  setGroups,
  addWorkflow,
  updateWorkflow,
  removeWorkflow,
  addApprovalLevel,
  updateApprovalLevel,
  removeApprovalLevel,
  addNewApprovalLevel,
  removeNewApprovalLevel,
  clearNewApprovalLevels,
  setDialogOpen,
  setEditing,
  setValidationErrors,
  setApprovalLevelDialogOpen,
  setSelectedApprovalLevel,
  setEditingApprovalLevel,
  setApprovalLevelValidationErrors,
  setSearchTerm,
  setFilters,
  setPagination,
  setStatistics,
  setSelectedWorkflowIds,
  toggleWorkflowSelection,
  selectAllWorkflows,
  deselectAllWorkflows,
  resetState,
} = workflowSlice.actions;

// Saga action types
export const FETCH_WORKFLOWS = 'workflow/fetchWorkflows';
export const FETCH_WORKFLOW_BY_ID = 'workflow/fetchWorkflowById';
export const CREATE_WORKFLOW = 'workflow/createWorkflow';
export const UPDATE_WORKFLOW = 'workflow/updateWorkflow';
export const DELETE_WORKFLOW = 'workflow/deleteWorkflow';
export const FETCH_ROLES = 'workflow/fetchRoles';
export const FETCH_USERS = 'workflow/fetchUsers';
export const FETCH_STATUSES = 'workflow/fetchStatuses';
export const FETCH_EVENT_TYPES = 'workflow/fetchEventTypes';
export const FETCH_GROUPS = 'workflow/fetchGroups';
export const CREATE_APPROVAL_LEVEL = 'workflow/createApprovalLevel';
export const UPDATE_APPROVAL_LEVEL = 'workflow/updateApprovalLevel';
export const DELETE_APPROVAL_LEVEL = 'workflow/deleteApprovalLevel';
export const BULK_ACTIVATE_WORKFLOWS = 'workflow/bulkActivateWorkflows';
export const BULK_DEACTIVATE_WORKFLOWS = 'workflow/bulkDeactivateWorkflows';
export const FETCH_WORKFLOW_STATISTICS = 'workflow/fetchWorkflowStatistics';

// Saga action creators
export const fetchWorkflows = () => ({ type: FETCH_WORKFLOWS });
export const fetchWorkflowById = (id: number) => ({ type: FETCH_WORKFLOW_BY_ID, payload: id });
export const createWorkflow = (workflow: CreateWorkflowRequest) => ({ type: CREATE_WORKFLOW, payload: workflow });
export const updateWorkflowAction = (workflow: UpdateWorkflowRequest) => ({ type: UPDATE_WORKFLOW, payload: workflow });
export const deleteWorkflow = (id: number) => ({ type: DELETE_WORKFLOW, payload: id });
export const fetchRoles = () => ({ type: FETCH_ROLES });
export const fetchUsers = () => ({ type: FETCH_USERS });
export const fetchStatuses = () => ({ type: FETCH_STATUSES });
export const fetchEventTypes = () => ({ type: FETCH_EVENT_TYPES });
export const fetchGroups = () => ({ type: FETCH_GROUPS });
export const createApprovalLevel = (level: CreateApprovalLevelRequest) => ({ type: CREATE_APPROVAL_LEVEL, payload: level });
export const updateApprovalLevelAction = (level: UpdateApprovalLevelRequest) => ({ type: UPDATE_APPROVAL_LEVEL, payload: level });
export const deleteApprovalLevel = (id: number) => ({ type: DELETE_APPROVAL_LEVEL, payload: id });
export const bulkActivateWorkflows = (ids: number[]) => ({ type: BULK_ACTIVATE_WORKFLOWS, payload: ids });
export const bulkDeactivateWorkflows = (ids: number[]) => ({ type: BULK_DEACTIVATE_WORKFLOWS, payload: ids });
export const fetchWorkflowStatistics = () => ({ type: FETCH_WORKFLOW_STATISTICS });

export default workflowSlice.reducer; 